// PRD.325 — the optional Form Header, a SPECIAL template mapping.
//
// NOT A FIELD. A header collects nothing from the patient, has no
// `form_instance_values` row and never appears in `fields_schema`. Putting it
// there would make it show up in Form mode, in binding pickers, in the
// "unmapped fields" warning and in every `flattenFields` consumer — for a piece
// of template chrome that only exists in Render mode.
//
// So it lives entirely in `form_template_mappings`, identified by a reserved
// `field_key` + `field_type`. Both columns are free text, and `config` is jsonb,
// so this needs NO schema change and PRD.321's clone copies it verbatim.
//
// NOT A FLOW ANCHOR either: its height is authoritative, it never paginates,
// never pushes downstream mappings and never repeats on a generated
// continuation page (PRD.324).
//
// Import-free so it runs under the node:test harness.

/**
 * Reserved identity for the header mapping.
 *
 * Double-underscored so it cannot collide with a real field key: `toFieldKey`
 * derives keys from labels and never produces this shape.
 */
export const FORM_HEADER_FIELD_KEY = '__form_header__';
export const FORM_HEADER_FIELD_TYPE = 'form_header';

/** Whether a mapping row is the Form Header rather than a patient field. */
export function isFormHeaderMapping(mapping: {
  field_key?: string | null;
  field_type?: string | null;
}): boolean {
  return (
    mapping.field_type === FORM_HEADER_FIELD_TYPE || mapping.field_key === FORM_HEADER_FIELD_KEY
  );
}

// ─── Presentation vocabulary ───
//
// SEMANTIC values, never raw CSS. A hex input would let an admin pick anything,
// including colours that vanish against their own letterhead, and would make
// "what does Large mean?" unanswerable centrally.

export const FORM_HEADER_COLORS = ['white', 'black', 'navy', 'blue', 'dark_gray'] as const;
export type FormHeaderColor = (typeof FORM_HEADER_COLORS)[number];

export const FORM_HEADER_SIZES = ['small', 'medium', 'large'] as const;
export type FormHeaderSize = (typeof FORM_HEADER_SIZES)[number];

/** Rendered colour per semantic name. ONE definition for all three surfaces. */
export const FORM_HEADER_COLOR_VALUES: Record<FormHeaderColor, string> = {
  white: '#ffffff',
  black: '#000000',
  navy: '#1e3a5f',
  blue: '#2563eb',
  dark_gray: '#374151',
};

export const FORM_HEADER_COLOR_LABELS: Record<FormHeaderColor, string> = {
  white: 'White',
  black: 'Black',
  navy: 'Navy',
  blue: 'Blue',
  dark_gray: 'Dark Gray',
};

/**
 * Rendered font size in CSS pixels, at the canonical page width.
 *
 * Shared by Map Fields, Preview and Render so a header cannot look like three
 * different sizes across the three surfaces.
 */
export const FORM_HEADER_SIZE_PX: Record<FormHeaderSize, number> = {
  small: 14,
  medium: 18,
  large: 24,
};

export interface FormHeaderConfig {
  /**
   * When true the header renders the CURRENT form name, resolved at render
   * time — never snapshotted. That is what makes a PRD.321 clone titled
   * "Root Canal Consent" show its own name rather than its source's.
   */
  useFormName: boolean;
  /**
   * Custom text. RETAINED while `useFormName` is on, so toggling back and forth
   * does not destroy what the admin typed.
   */
  headerText: string;
  alignment: 'left' | 'center' | 'right';
  color: FormHeaderColor;
  size: FormHeaderSize;
}

/**
 * Defaults for a newly placed header (PRD §Defaults).
 *
 * `white` because a header is usually placed over a coloured letterhead band —
 * the case that motivated this feature.
 */
export const FORM_HEADER_DEFAULTS: FormHeaderConfig = {
  useFormName: true,
  headerText: '',
  alignment: 'center',
  color: 'white',
  size: 'medium',
};

function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Read a header mapping's config. NEVER throws.
 *
 * Accepts a JSON string or an already-parsed object, and falls back to the
 * documented defaults for anything missing or unrecognised — a colour written
 * by a newer client is rendered as white rather than passed through to CSS.
 */
export function readFormHeaderConfig(rawConfig: unknown): FormHeaderConfig {
  let cfg: Record<string, unknown> = {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed: unknown = JSON.parse(rawConfig || '{}');
      if (parsed && typeof parsed === 'object') cfg = parsed as Record<string, unknown>;
    } catch {
      cfg = {};
    }
  } else if (rawConfig && typeof rawConfig === 'object') {
    cfg = rawConfig as Record<string, unknown>;
  }

  return {
    // Only an explicit `false` turns it off, so a config predating this key
    // still defaults to the form name.
    useFormName: cfg.useFormName !== false,
    headerText: typeof cfg.headerText === 'string' ? cfg.headerText : '',
    // Reuses PRD.323's `textAlign` key rather than inventing a parallel one.
    alignment: readEnum(cfg.textAlign, ['left', 'center', 'right'] as const, 'center'),
    color: readEnum(cfg.color, FORM_HEADER_COLORS, 'white'),
    size: readEnum(cfg.size, FORM_HEADER_SIZES, 'medium'),
  };
}

/** Merge changes back, preserving keys this feature does not own. */
export function writeFormHeaderConfig(
  rawConfig: unknown,
  patch: Partial<FormHeaderConfig>,
): string {
  let cfg: Record<string, unknown> = {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed: unknown = JSON.parse(rawConfig || '{}');
      if (parsed && typeof parsed === 'object') cfg = { ...(parsed as Record<string, unknown>) };
    } catch {
      cfg = {};
    }
  } else if (rawConfig && typeof rawConfig === 'object') {
    cfg = { ...(rawConfig as Record<string, unknown>) };
  }

  const next = { ...readFormHeaderConfig(rawConfig), ...patch };
  return JSON.stringify({
    ...cfg,
    useFormName: next.useFormName,
    headerText: next.headerText,
    textAlign: next.alignment,
    color: next.color,
    size: next.size,
  });
}

/**
 * The text to render.
 *
 * Resolved LIVE from the form definition, so renaming a form updates every
 * header that follows the name — and a clone shows the clone's name — without
 * touching a single mapping row.
 *
 * Custom text that is blank falls back to the form name rather than rendering
 * an empty header, which would look like a broken template.
 */
export function resolveFormHeaderText(
  config: FormHeaderConfig,
  formName: string | null | undefined,
): string {
  const name = (formName ?? '').trim();
  if (config.useFormName) return name;
  const custom = config.headerText.trim();
  return custom || name;
}
